import {BookingFormAirportState, BookingFormJourneyState, BookingFormPassengerState} from "../state"
import {
  BookingDetails,
  BookingGutsFragment,
  BookingStatusFragment,
  LocaleFragment,
  PastPassengers,
} from "../graphql/queries"
import {ScheduleFields} from "./ScheduleFields"
import {WithModalStore} from "../App"
import {hasCompleteJourney, PlaceFields} from "./PlaceFields"
import * as React from "react"
import {Form, Header, Loader, Popup, TextArea} from "semantic-ui-react"
import {bookingNotesHelpMessage} from "./BookingWizard"
import {needPeopleMover, PassengerFields, PassengerFieldsState} from "./PassengerFields"
import {adjustExtras, ExtrasFieldsWithButton} from "./ExtrasFields"
import {BookingStatusProp} from "./AddBookingWizard"
import * as moment from "moment"
import {bookingTimeTooClose} from "../model/time"
import {VehicleWidget} from "./VehicleWidget"
import {createDateTime} from "../model/charges"
import {
  emptyPassenger,
  passengersToInputPassengers,
  pastPassengerToPassenger,
} from "../model/passenger/passenger"
import {airportStopToInput, journeyToInputJourney} from "../model/journey"
import {emptyAirportState} from "./AirportStop"
import {MapState} from "./BookingDetails"
import {GraphQL} from "../graphql"
import {Booker} from "../model/BookingTypes"
import {PassengerFormData, PastPassenger} from "../model/passenger/types"

export interface BookingDetailsState {
  bookingDate: string
  bookingHour: number
  bookingMinute: number
  editingDate: boolean

  babySeatsForward: number
  babySeatsBackward: number
  boosterSeats: number

  passengerCount: string
  wantPeopleMover: boolean
  initialPassenger: PassengerFormData
  passengers: Array<BookingFormPassengerState>
  editingPassengerIndex: number | null
  editingPassenger: boolean

  journey: BookingFormJourneyState
  airportPickup: BookingFormAirportState
  airportDropoff: BookingFormAirportState

  // The locale isn't actually part of the booking. It's just a bit of state
  // to center the directions map when the journey hasn't been specified and
  // to say what common places appear in the place search. That's it.
  locale: LocaleFragment
}

export function bookingDateTime(details: BookingDetailsState | BookingGutsFragment) {
  return createDateTime(details.bookingDate, details.bookingHour, details.bookingMinute)
}

export function bookingDetailsStateToBookingDetails(details: BookingDetailsState): BookingDetails {
  const booking = adjustDetails(details)

  return {
    passengerCount: adjustPassengerCount(booking.passengerCount),
    babySeatsForward: booking.babySeatsForward,
    babySeatsBackward: booking.babySeatsBackward,
    boosterSeats: booking.boosterSeats,
    wantPeopleMover: booking.wantPeopleMover,
    bookingDateTime: bookingDateTime(booking),
    passengers: passengersToInputPassengers(booking.passengers),
    journey: journeyToInputJourney(booking.journey),
    airportPickup: airportStopToInput(booking.journey.origin!, booking.airportPickup),
    airportDropoff: airportStopToInput(booking.journey.destination!, booking.airportDropoff),
  }
}

export function bookingToBookingDetails(booking: BookingGutsFragment): BookingDetails {
  return {
    passengerCount: booking.passengerCount,
    babySeatsForward: booking.babySeatsForward,
    babySeatsBackward: booking.babySeatsBackward,
    boosterSeats: booking.boosterSeats,
    wantPeopleMover: booking.wantPeopleMover,
    bookingDateTime: bookingDateTime(booking),
    passengers: passengersToInputPassengers(booking.passengers),
    journey: journeyToInputJourney(booking.journey),
    airportPickup: airportStopToInput(
      booking.journey.origin!,
      booking.airportPickup || emptyAirportState(),
    ),
    airportDropoff: airportStopToInput(
      booking.journey.destination!,
      booking.airportDropoff || emptyAirportState(),
    ),
  }
}

export function detailsValid(
  state: BookingDetailsState,
  bookingStatus: BookingStatusFragment | null,
  isAdmin: boolean,
) {
  return (
    hasCompleteJourney(state) &&
    state.passengers.length > 0 &&
    validPassengerCount(state.passengerCount) &&
    !(!isAdmin && bookingStatus && bookingTimeTooClose(bookingStatus, moment()))
  )
}

export function detailsEditing(state: BookingDetailsState) {
  return state.editingPassenger || state.editingDate
}

export function adjustPassengerCount(value: string) {
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

export function validPassengerCount(value: string) {
  return adjustPassengerCount(value) > 0
}

export function adjustDetails(state: BookingDetailsState) {
  const passengerCount = adjustPassengerCount(state.passengerCount)
  const wantPeopleMover = needPeopleMover(passengerCount) || state.wantPeopleMover
  const extras = adjustExtras(state)
  return {...state, ...extras, wantPeopleMover}
}

export type StateMapper<T> = (state: T) => T
export type MapState<T> = (fn: StateMapper<T>) => void

interface Props extends WithModalStore, BookingStatusProp {
  state: BookingDetailsState
  mapState: MapState<BookingDetailsState> | null
  disabled: boolean
  onOptimizeWaypoints: (() => void) | null
  isAdmin: boolean
  forBooker: Booker | null
}

export function BookingDetails(props: Props) {
  const {
    state: stateOrig,
    mapState,
    disabled,
    modalStore,
    onOptimizeWaypoints,
    forBooker,
    isAdmin,
    bookingStatus,
    bookingStatusLoading,
  } = props
  const state = adjustDetails(stateOrig)
  return (
    <>
      <ScheduleFields
        bookingStatus={bookingStatus}
        bookingStatusLoading={bookingStatusLoading}
        state={state}
        disabled={disabled}
        mapState={
          mapState &&
          ((fn) =>
            mapState((state) => {
              return {...state, ...fn(state)}
            }))
        }
        isAdmin={isAdmin}
      />
      <PlaceFields
        modalStore={modalStore}
        wantFlightNumber={true}
        disabled={disabled}
        state={state}
        mapState={
          mapState &&
          ((fn) =>
            mapState((state) => {
              return {...state, ...fn(state)}
            }))
        }
        onOptimizeWaypoints={onOptimizeWaypoints}
      />
      {hasCompleteJourney(state) && (
        <>
          <VehicleWidget
            state={state}
            mapState={
              mapState &&
              ((fn) =>
                mapState((state) => {
                  return {...state, ...fn(state)}
                }))
            }
            disabled={disabled}
          />
          <PassengersWidget
            state={state}
            mapState={
              mapState &&
              ((fn) =>
                mapState((state) => {
                  return {...state, ...fn(state)}
                }))
            }
            disabled={disabled}
            forBooker={forBooker}
            isAdmin={isAdmin}
          />
          {state.passengers.length > 0 && (
            <>
              <ExtrasFieldsWithButton
                state={state}
                mapState={
                  mapState &&
                  ((fn) =>
                    mapState((state) => {
                      return {...state, ...fn(state)}
                    }))
                }
                disabled={disabled}
              />
            </>
          )}
        </>
      )}
    </>
  )
}

interface BookingNotesProps {
  notes: string
  onNotes: ((notes: string) => void) | null
  disabled: boolean
}

interface BookingNotesState {
  showBookingNotes: boolean
}

export function BookingNotesField(props: BookingNotesProps) {
  const {notes, onNotes, disabled} = props
  return (
    <>
      {!onNotes ? (
        notes ? (
          <div style={{whiteSpace: "pre-wrap"}}>{notes}</div>
        ) : (
          <i>None</i>
        )
      ) : (
        <TextArea
          style={{fontSize: "small"}}
          autoHeight
          rows={3}
          placeholder={bookingNotesHelpMessage}
          value={notes}
          onChange={(e, field) => {
            onNotes((field.value as string) || "")
          }}
          disabled={disabled}
        />
      )}
    </>
  )
}

export class BookingNotesWidget extends React.Component<BookingNotesProps, BookingNotesState> {
  constructor(props: BookingNotesProps) {
    super(props)
    this.state = {
      showBookingNotes: props.notes !== "",
    }
  }

  render() {
    const {onNotes, notes, disabled} = this.props
    if (this.state.showBookingNotes || notes !== "") {
      return (
        <>
          <Form.Field>
            <label>Booking notes</label>
            <BookingNotesField {...this.props} />
          </Form.Field>
        </>
      )
    } else if (onNotes) {
      return (
        <>
          <Popup
            trigger={
              <Form.Button
                disabled={disabled}
                content="Booking notes"
                icon="chevron right"
                labelPosition="right"
                onClick={(e) => {
                  e.preventDefault()
                  this.setState({showBookingNotes: true})
                }}
              />
            }
            content={bookingNotesHelpMessage}
            wide
          />
        </>
      )
    } else {
      return null
    }
  }
}

interface PassengersWidgetProps {
  state: PassengerFieldsState
  mapState: MapState<PassengerFieldsState> | null
  disabled: boolean
  isAdmin: boolean
  forBooker: Booker | null
}

function PassengersWidget(props: PassengersWidgetProps) {
  const {
    state: {passengers, editingPassenger: editing},
    state,
    mapState,
    disabled,
    isAdmin,
    forBooker,
  } = props
  return (
    <GraphQL.Query
      query={PastPassengers}
      variables={{forBookerId: forBooker && forBooker.id}}
      render={({data}) => {
        if (!data) {
          return <Loader active />
        }
        const {pastPassengers} = data
        return (
          <>
            {editing || passengers.length > 0 ? (
              <PassengerFields
                state={state}
                disabled={disabled}
                isAdmin={isAdmin}
                forBookerId={forBooker && forBooker.id}
                pastPassengers={pastPassengers}
                mapState={mapState}
              />
            ) : mapState ? (
              <>
                {forBooker && (
                  <Header style={{marginTop: "0rem"}} size="small" content="Enter Passengers" />
                )}
                <Form.Group>
                  {forBooker && (
                    <Form.Button
                      disabled={disabled}
                      content="Add me"
                      icon="chevron right"
                      labelPosition="right"
                      positive
                      onClick={(e) => {
                        e.preventDefault()
                        mapState((state) => passengerJustMe({state, forBooker, pastPassengers}))
                      }}
                    />
                  )}
                  <Popup
                    wide
                    content={"You must enter at least one passenger"}
                    trigger={
                      <Form.Button
                        disabled={disabled}
                        content={forBooker ? "Add anyone" : "Add passengers"}
                        icon="chevron right"
                        labelPosition="right"
                        positive
                        onClick={(e) => {
                          e.preventDefault()
                          mapState((state) => ({
                            ...state,
                            editingPassengerIndex: null,
                            editingPassenger: true,
                          }))
                        }}
                      />
                    }
                  />
                </Form.Group>
              </>
            ) : null}
          </>
        )
      }}
    />
  )
}

interface PassengerJustMeProps {
  state: PassengerFieldsState
  forBooker: Booker
  pastPassengers: Array<PastPassenger>
}

function passengerJustMe(props: PassengerJustMeProps): PassengerFieldsState {
  const {state, forBooker, pastPassengers} = props

  // This match should be exact because, presumably, we put it there in a previous booking
  // by using the final return below.
  const pastPassenger = pastPassengers.find((pp) => pp.name === forBooker.name)

  if (pastPassenger) {
    const hasMobile = Boolean(pastPassenger.mobile)
    const passengerFormData = pastPassengerToPassenger(pastPassenger)
    return {
      ...state,
      passengers: hasMobile ? [passengerFormData] : [],
      initialPassenger: hasMobile ? emptyPassenger() : passengerFormData,
      editingPassenger: !hasMobile,
    }
  }

  return {
    ...state,
    initialPassenger: {
      id: null,
      name: forBooker.name,
      email: forBooker.email,
      mobile: null,
      phone: null,
    },
    editingPassenger: true,
  }
}



// WEBPACK FOOTER //
// ./src/components/BookingDetails.tsx